import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import useSubscriptionStatus from '../hooks/useSubscriptionStatus';
import { useStripe } from '../contexts/StripeContext';
import { useSupabase } from '../contexts/SupabaseContext';
import {
  Check,
  ArrowLeft,
  Loader2,
  AlertCircle,
  RefreshCw,
  Calendar,
  ArrowUpRight,
  ArrowUp,
  ArrowDown,
  Crown,
  X,
  Clock,
  Settings,
  ExternalLink
} from 'lucide-react';
import { products } from '../stripe-config';

function useUserProfile() {
  const { user } = useAuth();
  const { supabase } = useSupabase();
  const [profile, setProfile] = React.useState<null | { plano_expira_em: string | null }>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setProfile(null);
      setLoading(false);
      return;
    }

    async function fetchProfile() {
      setLoading(true);
      const { data, error } = await supabase
        .from('profiles')
        .select('plano_expira_em')
        .eq('id', user.id)
        .maybeSingle();

      if (error) {
        setError(error.message);
        setProfile(null);
      } else {
        setProfile(data);
      }
      setLoading(false);
    }

    fetchProfile();
  }, [user, supabase]);

  return { profile, loading, error };
}

const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isTrialActive, isSubscribed, subscription, daysLeftInTrial, refresh } = useSubscriptionStatus();
  const { createCheckoutSession, cancelSubscription } = useStripe();

  const { profile, loading: loadingProfile, error: errorProfile } = useUserProfile();

  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelSuccess, setCancelSuccess] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const getPlanLevel = (priceId: string): number => {
    if (priceId === 'price_1RtW0mB0B3nriuwY1nJcHogh') return 3; // Premium
    if (priceId === 'price_1RtW7UB0B3nriuwYofTFEbYJ') return 2; // Professional
    return 1; // Basic
  };

  const getPlanAction = (productPriceId: string): 'current' | 'upgrade' | 'downgrade' | 'new' => {
    if (!isSubscribed || !subscription?.price_id) return 'new';
    if (subscription.price_id === productPriceId) return 'current';

    const currentLevel = getPlanLevel(subscription.price_id);
    const targetLevel = getPlanLevel(productPriceId);

    return targetLevel > currentLevel ? 'upgrade' : 'downgrade';
  };

  const isConfigurationError = (msg: string) => {
    return (
      msg.includes('Configuração do sistema') ||
      msg.includes('variáveis de ambiente') ||
      msg.includes('STRIPE_SECRET_KEY') ||
      msg.includes('não configurado')
    );
  };

  const handleSubscribe = async (priceId: string) => {
    if (!user) {
      setError('Você precisa estar logado para assinar um plano');
      return;
    }

    setLoading(priceId);
    setError(null);

    try {
      const result = await createCheckoutSession(priceId);

      if ('error' in result) {
        setError(result.error);
      } else if ('url' in result && result.url) {
        window.location.href = result.url;
      } else {
        setError('Erro inesperado: URL de checkout não recebida');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao processar assinatura');
    } finally {
      setLoading(null);
    }
  };

  const handleCancelSubscription = async () => {
    if (!user || !subscription) {
      setCancelError('Nenhuma assinatura ativa encontrada');
      return;
    }

    if (!confirm('Tem certeza que deseja cancelar sua assinatura? Você continuará tendo acesso até o final do período atual.')) {
      return;
    }

    setCancelLoading(true);
    setCancelError(null);
    setCancelSuccess(false);

    try {
      const result = await cancelSubscription();

      if (result.success) {
        setCancelSuccess(true);
        // Atualizar o status da assinatura após cancelamento bem-sucedido
        setTimeout(() => {
          refreshSubscriptionStatus();
        }, 1000);
      } else {
        // Melhor tratamento de erros específicos
        if (result.error?.includes('Configuração do sistema') || 
            result.error?.includes('variáveis de ambiente')) {
          setCancelError(`${result.error}\n\nEm ambiente de desenvolvimento, o cancelamento foi simulado localmente.`);
          setCancelSuccess(true); // Considera sucesso em dev
        } else {
          setCancelError(result.error || 'Erro ao cancelar assinatura');
        }
      }
    } catch (err: any) {
      console.error('Erro no handleCancelSubscription:', err);
      setCancelError('Erro inesperado ao cancelar assinatura. Tente novamente.');
    } finally {
      setCancelLoading(false);
    }
  };

  const refreshSubscriptionStatus = async () => {
    setRefreshing(true);
    try {
      await refresh();
    } catch (err) {
      console.error('Erro ao atualizar status da assinatura:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const handleRetry = (priceId: string) => {
    setError(null);
    handleSubscribe(priceId);
  };

  // Ajuste para usar a data do Supabase profile.plano_expira_em para próxima cobrança
  const getNextBillingDate = () => {
    if (!profile?.plano_expira_em) return null;
    const date = new Date(profile.plano_expira_em);
    if (isNaN(date.getTime())) return null;
    return date.toLocaleDateString('pt-BR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Calcula dias restantes para expiração baseado em profile.plano_expira_em
  const getDaysRemaining = () => {
    if (!profile?.plano_expira_em) return null;
    const expira = new Date(profile.plano_expira_em);
    if (isNaN(expira.getTime())) return null;
    const now = new Date();
    const diff = expira.getTime() - now.getTime();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days >= 0 ? days : 0;
  };

  // Calcula a duração total em dias com base no plano
  const getTotalDuration = (product: any) => {
    return product.durationMonths * 30; // Aproximação de meses para dias
  };

  return (
    <div className="py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          {(isTrialActive || isSubscribed) && (
            <button
              onClick={() => navigate('/')}
              className="btn-outline flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar ao Dashboard
            </button>
          )}
          
          <button
            onClick={refreshSubscriptionStatus}
            className="btn-outline flex items-center gap-2"
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Atualizando...' : 'Atualizar status'}
          </button>
        </div>

        <div className="text-left">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Escolha seu plano
          </h2>
          <p className="mt-4 text-lg text-gray-500 dark:text-gray-400">
            Comece a gerenciar seu time de futebol hoje mesmo
          </p>
        </div>

        {error && (
          <div className="mt-8 p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium">
                  {isConfigurationError(error) ? 'Configuração Necessária' : 'Erro ao processar pagamento'}
                </p>
                <div className="mt-1 text-sm whitespace-pre-line">{error}</div>

                {isConfigurationError(error) && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <a
                      href="https://supabase.com/dashboard"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-3 py-1 rounded-md hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                    >
                      <Settings className="w-4 h-4" />
                      Abrir Painel Supabase
                      <ExternalLink className="w-3 h-3" />
                    </a>
                    <a
                      href="https://dashboard.stripe.com/apikeys"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-3 py-1 rounded-md hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                    >
                      <Settings className="w-4 h-4" />
                      Obter Chave Stripe
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}

                {!isConfigurationError(error) && (error.includes('conexão') || error.includes('indisponível')) && (
                  <button
                    onClick={() => setError(null)}
                    className="mt-2 inline-flex items-center gap-1 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Tentar novamente
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Trial Status */}
        {isTrialActive && daysLeftInTrial !== null && (
          <div className="mt-8 p-4 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400 rounded-lg">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium">Período de Avaliação</p>
                <p className="mt-1 text-sm">
                  Você está no período de avaliação gratuita. Restam {daysLeftInTrial} {daysLeftInTrial === 1 ? 'dia' : 'dias'}.
                  {daysLeftInTrial <= 3 && (
                    <span className="block mt-1 font-medium">
                      Assine agora para continuar usando todos os recursos após o término do período de avaliação.
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Subscription Status */}
        {isSubscribed && subscription && (
          <div className="mt-8 p-4 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-lg">
            <div className="flex items-start gap-3">
              <Crown className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium">Assinatura Ativa</p>
                <p className="mt-1 text-sm">
                  Você tem uma assinatura ativa do plano {products.find(p => p.priceId === subscription.price_id)?.name || 'Presida'}.
                </p>
                {subscription.cancel_at_period_end ? (
                  <span className="block mt-1 text-yellow-600 dark:text-yellow-400">
                    Sua assinatura será cancelada ao final do período atual.
                  </span>
                ) : (
                  <>
                    <p className="block mt-1">
                      Próxima cobrança: {getNextBillingDate() || 'Data não disponível'}
                    </p>
                    <p className="block mt-1">
                      Restam {getDaysRemaining() ?? 0} {getDaysRemaining() === 1 ? 'dia' : 'dias'} até a próxima cobrança.
                    </p>
                  </>
                )}

                {!subscription.cancel_at_period_end && (
                  <div className="mt-3">
                    <button
                      onClick={handleCancelSubscription}
                      disabled={cancelLoading}
                      className="inline-flex items-center gap-1 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 border border-red-300 dark:border-red-700 px-3 py-1 rounded-md"
                    >
                      {cancelLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <X className="w-4 h-4" />
                      )}
                      {cancelLoading ? 'Cancelando...' : 'Cancelar assinatura'}
                    </button>

                    {cancelSuccess && (
                      <p className="mt-2 text-sm text-yellow-600 dark:text-yellow-400">
                        Assinatura cancelada com sucesso. Você continuará tendo acesso até o final do período atual.
                      </p>
                    )}

                    {cancelError && (
                      <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                        {cancelError}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Lista de planos */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-8">
          {products.map((product) => {
            const planAction = getPlanAction(product.priceId);
            const isLoadingThis = loading === product.priceId;
            const totalDuration = getTotalDuration(product);
            const currentPlan = products.find(p => p.priceId === subscription?.price_id);
            const currentDaysLeft = getDaysRemaining() || 0;

            return (
              <div
                key={product.id}
                className={`card p-8 w-full relative overflow-visible ${
                  planAction === 'current'
                    ? 'border-2 border-green-500 relative'
                    : product.name === 'Premium' ? 'border-2 border-primary-500 relative' : ''
                }`}
              >
                {planAction === 'current' ? (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                    Plano Atual
                  </div>
                ) : product.name === 'Premium' && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary-500 text-secondary-900 px-3 py-1 rounded-full text-sm font-medium">
                    Recomendado
                  </div>
                )}

                <div className="text-left"> 
                 <div className="flex items-center justify-start mb-4">
  <Crown className="w-8 h-8 text-primary-500" />
</div>

                  <h3 className="text-2xl font-bold">{product.name}</h3>
                  <div className="mt-2 flex items-center justify-start gap-2 text-sm text-gray-500">
  <Calendar className="w-4 h-4" />
  <span>
    {product.durationMonths === 1
      ? 'Mensal'
      : product.durationMonths === 12
        ? 'Anual'
        : `${product.durationMonths} meses`}
  </span>
</div>

                  <p className="mt-4 text-4xl font-bold">
                    {product.price}
                    <span className="text-base font-normal text-gray-500">
                      {product.durationMonths === 1 ? '/mês' : '/mês'}
                    </span>
                  </p>

            

{product.durationMonths === 12 && (
  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
    Cobrança mensal por 12 meses<br />
    <span className="text-[0.75rem]">
      (total de {product.price} × 12 = R$ {(parseFloat(product.price.replace('R$', '').replace(',', '.')) * 12).toFixed(2).replace('.', ',')})
    </span>
  </p>
)}

{product.durationMonths === 1 && (
  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
    Cobrança mensal<br />
    <span className="text-[0.75rem]">
      (renovação automática mensal)
    </span>
  </p>
)}
 
                  {/* Mostrar dias restantes após mudança de plano */}
                  {planAction !== 'current' && isSubscribed && currentPlan && (
                    <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                      {planAction === 'upgrade' ? (
                        <div className="flex flex-col items-center">
                          <span className="text-green-600 dark:text-green-400 font-medium">
                            Upgrade de plano
                          </span>
                          <span>
                            Você terá {currentDaysLeft} dias no novo plano
                          </span>
                        </div>
                      ) : planAction === 'downgrade' ? (
                        <div className="flex flex-col items-center">
                          <span className="text-yellow-600 dark:text-yellow-400 font-medium">
                            Downgrade de plano
                          </span>
                          <span>
                            Você terá {currentDaysLeft} dias no novo plano
                          </span>
                        </div>
                      ) : null}
                    </div>
                  )}

                  {planAction === 'current' ? (
                    <button
                      disabled
                      className="mt-8 w-full bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded-lg flex items-center justify-center gap-2 cursor-default"
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Plano Atual
                    </button>
                  ) : (
                    <button
                      onClick={() => handleSubscribe(product.priceId)}
                      disabled={isLoadingThis || !user}
                      className={`mt-8 w-full btn ${
                        planAction === 'upgrade'
                          ? 'bg-blue-500 hover:bg-blue-600 text-white'
                          : planAction === 'downgrade'
                            ? 'bg-orange-500 hover:bg-orange-600 text-white'
                            : 'btn-primary'
                      }`}
                    >
                      {isLoadingThis ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin mr-2" />
                          Processando...
                        </>
                      ) : !user ? (
                        'Faça login para assinar'
                      ) : (
                        <>
                          {planAction === 'upgrade' ? (
                            <>
                              <ArrowUp className="w-4 h-4 mr-2" />
                              Fazer Upgrade
                            </>
                          ) : planAction === 'downgrade' ? (
                            <>
                              <ArrowDown className="w-4 h-4 mr-2" />
                              Fazer Downgrade
                            </>
                          ) : (
                            <>
                              <ArrowUpRight className="w-4 h-4 mr-2" />
                              Assinar Agora
                            </>
                          )}
                        </>
                      )}
                    </button>
                  )}

                  <div className="mt-8 text-left">

                    <ul className="space-y-3">
                      {product.description.split(' - ').map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <Check className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PricingPage;